import React, { useEffect, useRef, useState } from 'react';
import { Empty, Spin, Typography } from 'antd';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { useClientConfigService } from '../../services/clients/ClientConfigServiceProvider';
import AssetImageViewer from '../common/AssetImageViewer';

// Renders the client's membership pricing sheet. A pricing sheet is configured
// as an S3 key on the client; the image itself is fetched as a short-lived
// presigned URL when the panel mounts.
const PricingSheetPanel: React.FC = () => {
    const { signup } = useClientConfig();
    const clientConfigService = useClientConfigService();

    const pricingSheetKey = signup?.membershipPricingSheetKey?.trim() ?? '';
    const pricingSheetConfigured = pricingSheetKey.length > 0;

    const [pricingUrl, setPricingUrl] = useState<string | null>(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    // Fetch the presigned URL exactly once per mount. A ref — not pricingUrl —
    // guards re-entry so a 404 (null URL) doesn't loop the effect.
    const pricingRequestedRef = useRef(false);

    // Drop the cached presigned URL and re-arm the one-shot fetch when the
    // tenant/service or the configured key changes.
    useEffect(() => {
        pricingRequestedRef.current = false;
        setPricingUrl(null);
    }, [clientConfigService, pricingSheetKey]);

    useEffect(() => {
        if (!pricingSheetConfigured || pricingRequestedRef.current) return;
        pricingRequestedRef.current = true;
        let cancelled = false;
        setPricingLoading(true);
        (async () => {
            try {
                const url = await clientConfigService.getSignupPricingSheetUrl();
                if (!cancelled) setPricingUrl(url);
            } finally {
                if (!cancelled) setPricingLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [pricingSheetConfigured, pricingSheetKey, clientConfigService]);

    if (!pricingSheetConfigured) {
        return (
            <div data-testid="tour-signup-pricing-sheet">
                <Empty description="No pricing sheet has been configured for this club." />
            </div>
        );
    }

    return (
        <div data-testid="tour-signup-pricing-sheet">
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Membership Pricing
            </Typography.Text>
            {pricingLoading && <Spin data-testid="tour-signup-pricing-loading" />}
            {!pricingLoading && pricingUrl && (
                <AssetImageViewer
                    url={pricingUrl}
                    alt="Membership pricing"
                    // ~50% larger than the default fit (840px / 112vh vs 560px /
                    // 75vh) — a tall sheet may now exceed the viewport, so the
                    // page scrolls to show it.
                    maxHeight="112vh"
                    data-testid="tour-signup-pricing-viewer"
                />
            )}
            {!pricingLoading && !pricingUrl && (
                <Empty description="The pricing sheet could not be loaded." />
            )}
        </div>
    );
};

export default PricingSheetPanel;
